import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/api';
import { X } from 'lucide-react';
import './Clients.css';

const AMC_TIERS = ['Bronze', 'Silver', 'Gold', 'Platinum'];
const DEPLOY_TYPES = ['cloud', 'on-prem', 'hybrid'];

const TIER_DEFAULTS = {
  Bronze:   { responseSlaHours: 48, resolutionSlaDays: 10, supportHours: '9x5' },
  Silver:   { responseSlaHours: 24, resolutionSlaDays: 5,  supportHours: '9x5' },
  Gold:     { responseSlaHours: 8,  resolutionSlaDays: 2,  supportHours: '9x7' },
  Platinum: { responseSlaHours: 2,  resolutionSlaDays: 1,  supportHours: '24x7' },
};

export default function ClientFormModal({ client, apps, onClose, onSaved }) {
  const isEdit = !!client;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const [form, setForm] = useState({
    name: '', code: '', contactEmail: '', contactPhone: '',
    deploymentType: 'cloud', notes: '',
    amcTier: { name: 'Silver', ...TIER_DEFAULTS['Silver'] },
    amcStartDate: '', amcEndDate: '',
    apps: [],
  });

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || '',
        code: client.code || '',
        contactEmail: client.contactEmail || '',
        contactPhone: client.contactPhone || '',
        deploymentType: client.deploymentType || 'cloud',
        notes: client.notes || '',
        amcTier: client.amcTier || { name: 'Silver', ...TIER_DEFAULTS['Silver'] },
        amcStartDate: client.amcStartDate ? client.amcStartDate.slice(0,10) : '',
        amcEndDate:   client.amcEndDate   ? client.amcEndDate.slice(0,10)   : '',
        apps: (client.apps || []).map(a => a._id || a),
      });
    }
  }, [client]);

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const ft = (key, val) => setForm(prev => ({ ...prev, amcTier: { ...prev.amcTier, [key]: val } }));

  const handleTierChange = (tier) => {
    setForm(prev => ({ ...prev, amcTier: { ...prev.amcTier, name: tier, ...TIER_DEFAULTS[tier] } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const payload = { ...form };
      if (payload.amcStartDate) payload.amcStartDate = new Date(payload.amcStartDate);
      if (payload.amcEndDate)   payload.amcEndDate   = new Date(payload.amcEndDate);
      if (isEdit) await adminService.updateClient(client._id, payload);
      else        await adminService.createClient(payload);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="client-form-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Client' : 'Add Client'}</h3>
          <button className="icon-btn" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} className="client-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-section-title">Basic Info</div>
          <div className="form-row">
            <div className="form-group"><label>Client Name *</label><input value={form.name} onChange={e => f('name', e.target.value)} required /></div>
            <div className="form-group"><label>Code * (e.g. ACME)</label><input value={form.code} onChange={e => f('code', e.target.value.toUpperCase())} required /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Contact Email *</label><input type="email" value={form.contactEmail} onChange={e => f('contactEmail', e.target.value)} required /></div>
            <div className="form-group"><label>Contact Phone</label><input value={form.contactPhone} onChange={e => f('contactPhone', e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label>Deployment Type</label>
            <select value={form.deploymentType} onChange={e => f('deploymentType', e.target.value)}>
              {DEPLOY_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div className="form-section-title">AMC Configuration</div>
          <div className="tier-selector">
            {AMC_TIERS.map(t => (
              <button key={t} type="button"
                className={`tier-btn ${form.amcTier.name === t ? 'active' : ''}`}
                onClick={() => handleTierChange(t)}>{t}
              </button>
            ))}
          </div>
          <div className="form-row">
            <div className="form-group"><label>Response SLA (hours)</label><input type="number" value={form.amcTier.responseSlaHours} onChange={e => ft('responseSlaHours', +e.target.value)} /></div>
            <div className="form-group"><label>Resolution SLA (days)</label><input type="number" value={form.amcTier.resolutionSlaDays} onChange={e => ft('resolutionSlaDays', +e.target.value)} /></div>
            <div className="form-group"><label>Support Hours</label><input value={form.amcTier.supportHours} onChange={e => ft('supportHours', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>AMC Start Date</label><input type="date" value={form.amcStartDate} onChange={e => f('amcStartDate', e.target.value)} /></div>
            <div className="form-group"><label>AMC End Date</label><input type="date" value={form.amcEndDate} onChange={e => f('amcEndDate', e.target.value)} /></div>
          </div>

          <div className="form-group"><label>Notes</label><textarea rows={3} value={form.notes} onChange={e => f('notes', e.target.value)} /></div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Client'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
